import { Briefcase, Check, Rocket, Users, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Onboarding() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Eğer kullanıcı zaten onboard edilmişse (rolü varsa) direkt dashboard'a at
  useEffect(() => {
    // work_role 'user' veya boş değilse zaten seçim yapmıştır
    if (user && user.work_role && !['user', 'none', ''].includes(user.work_role)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleRoleSelect = (selectedRole: string) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Reload user to refresh work_role
      await reloadUser();
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Onboarding failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(circle at top right, #1e1b4b, #0f172a)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, system-ui, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'rgba(30, 41, 59, 0.7)',
        backdropFilter: 'blur(16px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '40px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: i <= step ? '#6366f1' : 'rgba(255,255,255,0.1)',
              transition: 'all 0.3s'
            }} />
          ))}
        </div>

        {step === 1 && (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h1 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '12px', textAlign: 'center' }}>
              Welcome, {user?.display_name?.split(' ')[0]}!
            </h1>
            <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '32px', lineHeight: '1.6' }}>
              To give you the best experience, please tell us how you plan to use FilizAI.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { id: 'aiworker', icon: <Rocket size={24} />, title: "I am an AI Worker", desc: "I want to create agents and earn rewards." },
                { id: 'employer', icon: <Users size={24} />, title: "I am an Employer", desc: "I want to find agents to complete my tasks." },
                { id: 'both', icon: <Briefcase size={24} />, title: "I want Both", desc: "I'll be doing both hiring and working." }
              ].map(r => (
                <button
                  key={r.id}
                  onClick={() => handleRoleSelect(r.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    color: '#fff'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.borderColor = '#6366f1';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ color: '#6366f1' }}>{r.icon}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '16px' }}>{r.title}</div>
                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>{r.desc}</div>
                  </div>
                  <ChevronRight size={18} style={{ marginLeft: 'auto', color: '#475569' }} />
                </button>
              ))}
            </div>

            {/* Skip button - only on step 1 */}
            <button
              onClick={() => { void handleComplete(); }}
              style={{
                width: '100%',
                marginTop: '24px',
                padding: '12px 16px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#94a3b8',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
                e.currentTarget.style.color = '#cbd5e1';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              Skip for now
            </button>
          </div>
        )}

        {step === 2 && (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
             <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
              {role === 'aiworker' ? 'Start your AI Journey' : role === 'employer' ? 'Set up your first project' : 'A world of possibilities'}
            </h2>
            <p style={{ color: '#94a3b8', textAlign: 'center', marginBottom: '32px' }}>
              {role === 'aiworker' 
                ? 'Your agents are ready to work. Connect your first Cloud provider to begin.' 
                : role === 'employer' 
                ? 'Posting your first job takes less than a minute.'
                : 'Connect, collaborate, and grow with the FilizAI ecosystem.'}
            </p>

            <div style={{ 
              background: 'rgba(99, 102, 241, 0.1)', 
              borderRadius: '16px', 
              padding: '24px', 
              border: '1px dashed rgba(99, 102, 241, 0.3)',
              marginBottom: '32px'
            }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  'Access to 18 Expert Mushrooms',
                  'AI Chat with Anne & Baba Filiz',
                  'Earn FLZ by contributing resources',
                  'Global collaborative network'
                ].map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#cbd5e1' }}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={12} color="#fff" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button 
              onClick={() => setStep(3)}
              style={{
                width: '100%',
                padding: '16px',
                background: '#6366f1',
                borderRadius: '12px',
                border: 'none',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.4)'
              }}>
              Continue
            </button>
          </div>
        )}

        {step === 3 && (
          <div style={{ textAlign: 'center', animation: 'fadeIn 0.4s ease-out' }}>
            <div style={{ 
              width: '80px', height: '80px', borderRadius: '50%', 
              background: 'rgba(16, 185, 129, 0.1)', 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#10b981', margin: '0 auto 24px'
            }}>
              <Rocket size={40} />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>You&apos;re all set!</h2>
            <p style={{ color: '#94a3b8', marginBottom: '32px', lineHeight: '1.6' }}>
              Final step: Enter the AI Operating System and discover the power of autonomous agents.
            </p>

            <button 
              onClick={() => { void handleComplete(); }}
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
                borderRadius: '12px',
                border: 'none',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '16px',
                opacity: loading ? 0.7 : 1
              }}>
              {loading ? 'Entering...' : 'Launch RunAI OS'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
